import {
  FirestoreDataConverter,
  doc,
  getFirestore,
  type DocumentData,
  type Timestamp,
} from 'firebase/firestore';
import {CourseId, type UserId} from 'types/common';
import {FormInfo, ApplicationFieldType} from './formData';
import {
  isApplicationStatus,
  type ApplicationStatus,
} from 'course/application/general/utils/checkStatus';
import type {ApplicationType} from 'course/application/firebase/models/applicationData';
import {
  validateObjectField,
  validateOptionalPrimitiveField,
} from 'utils/general';
import type {CountryCode} from 'data/countries/country_code_to_names';

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const SUPPORTED_APPLICANT_RESPONSE_TYPES = [
  'text',
  'numeric',
  'options',
  'date',
  'geolocation',
  'debug',
] as const;
export type ApplicantResponseType =
  (typeof SUPPORTED_APPLICANT_RESPONSE_TYPES)[number];

export type TextResponse = string;
export type NumericResponse = string;
export type OptionsResponse = string[];

// SL's only
export type DebugResponse = Record<string, string>;
export const SPECIAL_FIELD_NAMES = {
  DEBUG_ANSWERS: 'debugAnswers',
} as const;

export interface DateResponse {
  day: number;
  month: number;
  year: number;
}

export interface GeolocationResponse {
  locality: string;
  coordinates?: string;
}

export type ApplicantResponse =
  | TextResponse
  | NumericResponse
  | OptionsResponse
  | DateResponse
  | GeolocationResponse
  | DebugResponse;

export interface ApplicantFieldData {
  fieldId: string;
  fieldResponse: ApplicantResponse;
}

export interface ApplicantData {
  fields: ApplicantFieldData[];

  /**
   * The status of the application. If not set, the application has not been
   * started.
   */
  appStatus?: ApplicationStatus;

  /**
   * Whether the application has been sent a confirmation email that their
   * application has been received. Fired once when the application is complete.
   * See `useAppStatusServerRecorder` for more details.
   */
  sentConfirmationEmail?: boolean;

  //////////////////////////////
  // Student application data
  //////////////////////////////
  /**
   * Whether the student has read the about course page.
   */
  hasReadAboutCourse?: boolean;

  //////////////////////////////
  // Section leader application data
  //////////////////////////////
  /**
   * Whether the section leader has read the about job page.
   */
  hasReadAboutJob?: boolean;
  /**
   * Maps from the title of a debugging question to the answer.
   * Currently only used for section leader applications.
   */
  debugAnswers?: Record<string, string>;

  /**
   * Whether the section leader has requested auto accept.
   */
  hasRequestedAutoAccept?: boolean;

  /**
   * @deprecated Used in CIP5 and earlier.
   * The country of the section leader.
   */
  country?: {
    /**
     * @deprecated Used in CIP5 and earlier.
     * The country code.
     */
    code: CountryCode;
    /**
     * @deprecated Used in CIP5 and earlier.
     * The english name of the country.
     */
    eng_name: string;
    /**
     * @deprecated Used in CIP5 and earlier.
     * The native name of the country.
     */
    native_name: string;
  };

  /**
   * @deprecated Used in CIP5 and earlier.
   * The karel debug suggestions from the section leader.
   */
  karelDebug?: string;

  /**
   * @deprecated Used in CIP5 and earlier.
   * The karel debug suggestions from the section leader.
   */
  pythonDebug?: string;

  /**
   * When the applicant first finished the application. Stamped once by
   * `updateAppStatus` and never cleared, so it outlives a later status
   * change. Reads back as a Firestore Timestamp.
   */
  completedDate?: Timestamp | Date;

  /**
   * Whether that first finish came in while the application was taking late
   * applications for this applicant (see `getApplicantWindow`). Stamped
   * together with `completedDate`.
   */
  isLateSubmission?: boolean;

  /**
   * TODO: add additional fields here.
   */
  [additionalFields: string]: unknown;
}

/**
 * Builds the Firestore path to a user's saved answers for one application.
 *
 * @param userId - The uid of the applicant.
 * @param courseId - The course being applied to (the target course).
 * @param applicationType - Whether these are student or section-leader answers;
 *   selects the `student`/`sectionLeader` document name.
 * @returns The document path
 *   `users/<userId>/<courseId>/<student|sectionLeader>Application`.
 */
export const getUserApplicationDataFirebasePath = ({
  userId,
  courseId,
  applicationType,
}: {
  userId: UserId;
  courseId: CourseId;
  applicationType: ApplicationType;
}): string => {
  return `users/${userId}/${courseId}/${applicationTypeToPerUserDataName(applicationType)}Application`;
};

const applicationTypeToPerUserDataName = (
  applicationType: ApplicationType,
): 'student' | 'sectionLeader' => {
  switch (applicationType) {
    case 'student':
      return 'student';
    case 'sl':
      return 'sectionLeader';
  }
};

/**
 * Decodes a stored applicant document into {@link ApplicantData}.
 *
 * @param params.data - The stored document's data, however it was read.
 * @param params.applicationType - Which application the document belongs to.
 * @param params.formInfo - The form the applicant answered. Omitted leaves
 *     `fields` empty, since answers are only recognized against their form.
 * @return The decoded applicant data; `{fields: []}` for a non-object.
 * @throws Never; invalid fields are skipped with a warning.
 */
export function parseApplicantData({
  data,
  applicationType,
  formInfo,
}: {
  data: DocumentData | null | undefined;
  applicationType: ApplicationType;
  formInfo?: FormInfo;
}): ApplicantData {
  if (!data || typeof data !== 'object') {
    return {fields: []};
  }

  // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  let fields: ApplicantFieldData[] = [];

  if (formInfo) {
    Object.entries(data).forEach(([fieldId, fieldResponse]) => {
      if (!fieldResponse) {
        console.warn(
          'Invalid applicant field data (null/undefined), skipping',
        );
      } else {
        if (isApplicantFieldData(fieldResponse, fieldId, formInfo)) {
          fields.push({fieldId, fieldResponse});
        } else {
          console.warn(
            `Failed to validate applicant field with id ${fieldId}, skipping`,
          );
        }
      }
    });
  } else {
    console.warn('No form info provided, the `fields` field will be empty');
  }

  const {
    appStatus,
    hasReadAboutCourse,
    hasReadAboutJob,
    hasRequestedAutoAccept,
    debugAnswers,
    sentConfirmationEmail,
    ...additionalFields
  } = data;

  if (!isApplicationStatus(appStatus)) {
    console.warn('Invalid application status', appStatus);
  }
  if (!validateOptionalPrimitiveField(sentConfirmationEmail, 'boolean')) {
    console.warn('Invalid sentConfirmationEmail', sentConfirmationEmail);
  }

  if (applicationType === 'student') {
    if (!validateOptionalPrimitiveField(hasReadAboutCourse, 'boolean')) {
      console.warn('Invalid hasReadAboutCourse', hasReadAboutCourse);
    }
  } else if (applicationType === 'sl') {
    if (!validateOptionalPrimitiveField(hasReadAboutJob, 'boolean')) {
      console.warn('Invalid hasReadAboutJob', hasReadAboutJob);
    }
    if (
      !validateObjectField({
        field: debugAnswers,
        validateElement: (key, value) => typeof value === 'string',
        isOptional: true,
      })
    ) {
      console.warn('Invalid debugAnswers', debugAnswers);
    }
  }

  return {
    fields,
    appStatus,
    hasReadAboutCourse,
    hasReadAboutJob,
    hasRequestedAutoAccept,
    debugAnswers,
    ...additionalFields,
  };
}

export const applicantDataConverter = (
  applicationType: ApplicationType,
  formInfo?: FormInfo,
): FirestoreDataConverter<ApplicantData> => ({
  toFirestore: (data: ApplicantData) => {
    const exportedData = {...data};
    for (const field of data?.fields ?? []) {
      exportedData[field.fieldId] = field.fieldResponse;
    }
    delete exportedData.fields;
    return exportedData;
  },
  fromFirestore: (snapshot, options) =>
    parseApplicantData({
      data: snapshot.data(options),
      applicationType,
      formInfo,
    }),
});

/**
 * Gets the actual applicant data for a given user and course.
 *
 * WARNING: Since this call parse the data dynamically via the formInfo, it will
 * by default recalculate the data every frame. It should be used in conjunction
 * with useMemo to avoid unnecessary recalculations.
 */
export const getApplicantDataRef = ({
  courseId,
  userId,
  applicationType,
  formInfo,
}: {
  courseId: CourseId;
  userId: UserId;
  applicationType: ApplicationType;
  formInfo?: FormInfo;
}) => {
  const db = getFirestore();
  return doc(
    db,
    getUserApplicationDataFirebasePath({userId, courseId, applicationType}),
  ).withConverter(applicantDataConverter(applicationType, formInfo));
};

// Validation functions for applicant response types
const isTextResponse = (object: unknown): object is TextResponse => {
  return typeof object === 'string';
};

const isNumericResponse = (object: unknown): object is NumericResponse => {
  return typeof object === 'string';
};

const isOptionsResponse = (object: unknown): object is OptionsResponse => {
  return (
    Array.isArray(object) && object.every(item => typeof item === 'string')
  );
};

const isDateResponse = (object: unknown): object is DateResponse => {
  if (!object || typeof object !== 'object') return false;
  const dateResponse = object as DateResponse;

  return (
    typeof dateResponse.day === 'number' &&
    typeof dateResponse.month === 'number' &&
    typeof dateResponse.year === 'number' &&
    Number.isInteger(dateResponse.day) &&
    Number.isInteger(dateResponse.month) &&
    Number.isInteger(dateResponse.year) &&
    dateResponse.day >= 1 &&
    dateResponse.day <= 31 &&
    dateResponse.month >= 1 &&
    dateResponse.month <= 12 &&
    dateResponse.year > 0
  );
};

const isGeolocationResponse = (
  object: unknown,
): object is GeolocationResponse => {
  if (!object || typeof object !== 'object') return false;
  const geoResponse = object as GeolocationResponse;

  return (
    typeof geoResponse.locality === 'string' &&
    (geoResponse.coordinates === undefined ||
      typeof geoResponse.coordinates === 'string')
  );
};

const isDebugResponse = (object: unknown): object is DebugResponse => {
  if (!object || typeof object !== 'object' || Array.isArray(object))
    return false;

  const debugResponse = object as Record<string, unknown>;

  // Check that all values are strings
  return Object.values(debugResponse).every(value => typeof value === 'string');
};

const getExpectedResponseType = (
  fieldType: ApplicationFieldType,
): ApplicantResponseType => {
  switch (fieldType) {
    case 'text':
    case 'longtext':
    case 'dropdown':
    case 'radio':
    case 'linear_scale':
    case 'audio_input':
      return 'text';
    case 'numeric':
      return 'numeric';
    case 'multicheckbox':
    case 'singlecheckbox':
      return 'options';
    case 'date':
      return 'date';
    case 'geolocation':
      return 'geolocation';
    default:
      return 'text';
  }
};

const isApplicantResponse = (
  object: unknown,
  expectedType: ApplicantResponseType,
): object is ApplicantResponse => {
  switch (expectedType) {
    case 'text':
      return isTextResponse(object);
    case 'numeric':
      return isNumericResponse(object);
    case 'options':
      return isOptionsResponse(object);
    case 'date':
      return isDateResponse(object);
    case 'geolocation':
      return isGeolocationResponse(object);
    case 'debug':
      return isDebugResponse(object);
    default:
      return false;
  }
};

// Validation function that requires form data for context
const isApplicantFieldData = (
  fieldResponse: unknown,
  fieldId: string,
  formInfo?: FormInfo,
): boolean => {
  // Handle special debug answers field
  if (fieldId === SPECIAL_FIELD_NAMES.DEBUG_ANSWERS) {
    return isApplicantResponse(fieldResponse, 'debug');
  }

  if (!formInfo) {
    console.warn(`No form data provided for validation of field ${fieldId}`);
    return false;
  }

  // Find the corresponding form field (check both custom and required fields)
  const formField =
    formInfo.fields.find(field => field.id === fieldId) ??
    formInfo.requiredFields?.find(field => field.id === fieldId);
  if (!formField) {
    console.warn(`No form field found for applicant field ${fieldId}`);
    return false;
  }

  const expectedType = getExpectedResponseType(formField.type);
  return isApplicantResponse(fieldResponse, expectedType);
};
